import { useTheme } from '@mui/material';
import { BarSeriesType } from '@mui/x-charts';
import dayjs, { Dayjs } from 'dayjs';
import { useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@verantos-dev/core-annotation-tool';

import {
  useGetAnnotationProgressBatchWiseDateRangeQuery,
  useGetHolidaysDataQuery,
} from '../../api/metricsApiSlice';
import { getAllAnnotatorsOption } from '../../constants';
import {
  setSelectedBatchwiseProgressTab,
  setBatchwiseEndDate,
  setBatchwiseStartDate,
} from '../../state/metricsSlice';
import {
  selectSelectedAnnotatorId,
  selectSelectedBatchwiseProgressTab,
  selectSelectedProjectId,
  selectBatchwiseProgressEndDate,
  selectBatchwiseProgressStartDate,
} from '../../state/selector';
import { BatchwiseAnnotationProgress } from '../../types/responseTypes';

interface ChartDataType {
  batch: string;
  pending: number;
  completed: number;
  [key: string]: unknown;
}
interface UseBatchProgressProps {
  annotationProgressBatchWiseData: BatchwiseAnnotationProgress[];
}

interface UseBatchProgressResults {
  chartData: ChartDataType[];
  chartSeries: BarSeriesType[];
  selectedBatchwiseProgressTab: string;
  handleTabChange: (tab: string) => void;
  isBatchwiseAnnotationProgressDateRangeLoading: boolean;
  minDate: string;
  maxDate: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  handleChangeStartDate: (val: Dayjs | null) => void;
  handleChangeEndDate: (val: Dayjs | null) => void;
  shouldDisableStartDate: (day: Dayjs) => boolean;
}

export const useBatchProgress = ({
  annotationProgressBatchWiseData,
}: UseBatchProgressProps): UseBatchProgressResults => {
  const chartData = annotationProgressBatchWiseData.map((item) => ({
    batch: item.batchName,
    pending: item.pendingTaskCount,
    completed: item.completedTaskCount,
  }));

  const selectedBatchwiseProgressTab = useAppSelector(selectSelectedBatchwiseProgressTab);
  const dispatch = useAppDispatch();
  const handleTabChange = (tab: string) => {
    dispatch(setSelectedBatchwiseProgressTab(tab));
  };
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const chartSeries: BarSeriesType[] = [
    {
      type: 'bar',
      dataKey: 'completed',
      label: formatMessage({ id: 'metrics.dashboard.batchProgress.completed.label' }),
      stack: 'total',
      color: theme.palette.primary.dark,
    },
    {
      type: 'bar',
      dataKey: 'pending',
      label: formatMessage({ id: 'metrics.dashboard.batchProgress.pending.label' }),
      stack: 'total',
      color: theme.palette.primary.light,
    },
  ];

  const projectId = useAppSelector(selectSelectedProjectId);
  const userId = useAppSelector(selectSelectedAnnotatorId);
  const annotatorAllOption = getAllAnnotatorsOption(formatMessage);
  const {
    data: batchwiseAnnotationProgressDateRange,
    isLoading: isBatchwiseAnnotationProgressDateRangeLoading,
    isFetching: isBatchwiseAnnotationProgressDateRangeFetching,
  } = useGetAnnotationProgressBatchWiseDateRangeQuery(
    {
      projectId: projectId ?? '',
      userId: userId === annotatorAllOption.id ? undefined : userId || undefined,
    },
    { skip: !projectId }
  );

  const startDate = useAppSelector(selectBatchwiseProgressStartDate);
  const endDate = useAppSelector(selectBatchwiseProgressEndDate);

  useEffect(() => {
    if (
      !batchwiseAnnotationProgressDateRange?.data?.minDate ||
      !batchwiseAnnotationProgressDateRange?.data?.maxDate
    )
      return;
    dispatch(
      setBatchwiseStartDate(dayjs(batchwiseAnnotationProgressDateRange.data?.minDate).toISOString())
    );
    dispatch(
      setBatchwiseEndDate(dayjs(batchwiseAnnotationProgressDateRange.data?.maxDate).toISOString())
    );
  }, [batchwiseAnnotationProgressDateRange]);

  const handleChangeStartDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setBatchwiseStartDate(val.toISOString()));
    }
  };
  const handleChangeEndDate = (val: Dayjs | null) => {
    if (val) {
      dispatch(setBatchwiseEndDate(val.toISOString()));
    }
  };

  const { data: holidaysData } = useGetHolidaysDataQuery();

  const holidayDates = useMemo<Set<string>>(() => {
    if (!holidaysData?.data) return new Set();
    return new Set(holidaysData.data.map((h) => dayjs(h.date).format('YYYY-MM-DD')));
  }, [holidaysData]);

  const shouldDisableStartDate = (day: Dayjs) => {
    const dow = day.day();
    if (dow === 0 || dow === 6) return true;
    return holidayDates.has(day.format('YYYY-MM-DD'));
  };

  return {
    chartData,
    chartSeries,
    selectedBatchwiseProgressTab,
    handleTabChange,
    isBatchwiseAnnotationProgressDateRangeLoading:
      isBatchwiseAnnotationProgressDateRangeLoading ||
      isBatchwiseAnnotationProgressDateRangeFetching,
    minDate: batchwiseAnnotationProgressDateRange?.data?.minDate || '',
    maxDate: batchwiseAnnotationProgressDateRange?.data?.maxDate || '',
    startDate: startDate ? dayjs(startDate) : null,
    endDate: endDate ? dayjs(endDate) : null,
    handleChangeStartDate,
    handleChangeEndDate,
    shouldDisableStartDate,
  };
};
